import { makeObservable, observable } from 'mobx'

import { IPosition, ISitemapPin } from '~/client/graph'
import MapViewLocationIcon from '~/client/src/shared/enums/SitemapAttributeIcon'

import { areObjectsEqual } from '../../../utils/util'
import { IBoundingBox } from './MapViewItemBase'

const DEFAULT_IS_DISPLAYED = true
const DEFAULT_POSITION = {
  x: 50,
  y: 50,
}
const MAX_PERCENT = 100

const DEFAULT_MID_ICON_SIZE = {
  width: 24,
  height: 24,
}
const DEFAULT_LARGE_ICON_SIZE = {
  width: 32,
  height: 32,
}

const ICON_SIZE_MAP = {
  [MapViewLocationIcon.Gate]: DEFAULT_LARGE_ICON_SIZE,
  [MapViewLocationIcon.Zone]: {
    width: 22,
    height: 32,
  },
  [MapViewLocationIcon.Building]: {
    width: 24,
    height: 32,
  },
  [MapViewLocationIcon.Route]: DEFAULT_LARGE_ICON_SIZE,
  [MapViewLocationIcon.Equipment]: DEFAULT_LARGE_ICON_SIZE,
  [MapViewLocationIcon.Level]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Area]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Logistics]: {
    width: 18,
    height: 26,
  },
  [MapViewLocationIcon.Bathroom]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Break]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Dumpster]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Elevator]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Shaft]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Entrance]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.HandWash]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Medical]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.MeetingPoint]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Parking]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Smoking]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Stairs]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Temperature]: {
    width: 18,
    height: 24,
  },
  [MapViewLocationIcon.Tent]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Walkway]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.ElectricalRoom]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Trailer]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Crane]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Hoist]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.AerialLift]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.Gradall]: DEFAULT_MID_ICON_SIZE,
  [MapViewLocationIcon.ForkLift]: DEFAULT_MID_ICON_SIZE,
}

const LABEL_OFFSET = 2.5
const DEGREE_IN_RADIANS = Math.PI / 180

export default class SitemapIconProperties {
  @observable public isDisplayed: boolean = null
  @observable public position: IPosition = null

  public constructor(isDisplayed?: boolean, position?: IPosition) {
    this.isDisplayed =
      typeof isDisplayed === 'boolean' ? isDisplayed : DEFAULT_IS_DISPLAYED
    this.position = position || DEFAULT_POSITION
    makeObservable(this)
  }

  public isEqual(properties: SitemapIconProperties) {
    return areObjectsEqual(this, properties)
  }

  public getBoundingBox(
    iconName: MapViewLocationIcon,
    containerWidth: number,
    containerHeight: number,
  ): IBoundingBox {
    const { width, height } = ICON_SIZE_MAP[iconName] ?? DEFAULT_MID_ICON_SIZE

    const x = (containerWidth * this.position.x) / MAX_PERCENT
    const y = (containerHeight * this.position.y) / MAX_PERCENT

    return {
      x: x - width / 2,
      y: y - height / 2,
      width,
      height,
    }
  }

  public isValid(): boolean {
    return !!this.position
  }

  public move(x: number, y: number): IPosition[] {
    this.position = {
      x: this.position.x + x,
      y: this.position.y + y,
    }
    return [this.position]
  }

  public calculateLabelPosition(): IPosition {
    return {
      x: this.position.x,
      y: this.position.y + LABEL_OFFSET,
    }
  }

  public isPositionEqual(position: IPosition) {
    if (!this.position) {
      return !position
    }
    return (
      position &&
      this.position.x === position.x &&
      this.position.y === position.y
    )
  }

  public setPosition(position: IPosition) {
    this.position = position
  }

  public toggleIsDisplayed() {
    this.isDisplayed = !this.isDisplayed
  }

  public isDisplayDataEqual(pin: ISitemapPin) {
    return (
      !pin.isHidden === this.isDisplayed && this.isPositionEqual(pin.position)
    )
  }

  public getDisplayData(): ISitemapPin {
    return {
      isHidden: !this.isDisplayed,
      position: {
        x: this.position.x,
        y: this.position.y,
      },
    }
  }

  public scale(originPoint: IPosition, scaleX: number, scaleY: number) {
    this.position.x = scaleX * (this.position.x - originPoint.x) + originPoint.x
    this.position.y = scaleY * (this.position.y - originPoint.y) + originPoint.y
  }

  public rotate(center: IPosition, angle: number) {
    let { x, y } = this.position

    x -= center.x
    y -= center.y

    const cos = Math.cos(angle * DEGREE_IN_RADIANS)
    const sin = Math.sin(angle * DEGREE_IN_RADIANS)

    const newX = x * cos - y * sin
    const newY = x * sin + y * cos

    this.position = {
      x: newX + center.x,
      y: newY + center.y,
    }
  }

  public copy(): SitemapIconProperties {
    return new SitemapIconProperties(
      this.isDisplayed,
      this.position && {
        x: this.position.x,
        y: this.position.y,
      },
    )
  }
}
